import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type KeywordFeedbackDetails,
  type KeywordFeedbackRequest,
} from "reedy-data/api";
import { useApi } from "hooks/useApi";
import { logError } from "shared/services/ErrorReporting";
import { toast } from "utils/toast";

export type KeywordKey = string;

export interface KeywordFeedbackEntry {
  feedbackId: string;
  details?: KeywordFeedbackDetails;
  discarded: boolean;
}

export const keyForKeyword = (keyword: string): KeywordKey =>
  keyword.trim().toLowerCase();

type State = Map<KeywordKey, KeywordFeedbackEntry>;

const cloneWith = (
  state: State,
  key: KeywordKey,
  entry: KeywordFeedbackEntry,
): State => {
  const next = new Map(state);
  next.set(key, entry);
  return next;
};

const cloneWithout = (state: State, key: KeywordKey): State => {
  const next = new Map(state);
  next.delete(key);
  return next;
};

export const useKeywordFeedback = (productId: string | undefined) => {
  const { t } = useTranslation(["components"]);
  const api = useApi();
  const [entries, setEntries] = useState<State>(() => new Map());
  // Mirrors `entries` for sync reads from inside callbacks — keeps submit /
  // discard from depending on `entries` (which would recreate them on every
  // state change and force downstream useCallbacks to re-run).
  const entriesRef = useRef<State>(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);
  // Same UUID is reused across submit → edit → discard so all writes upsert
  // a single Firestore doc per (user, keyword, session).
  const idsRef = useRef<Map<KeywordKey, string>>(new Map());

  const ensureFeedbackId = useCallback((key: KeywordKey): string => {
    const existing = idsRef.current.get(key);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    idsRef.current.set(key, fresh);
    return fresh;
  }, []);

  const put = useCallback(
    async (body: KeywordFeedbackRequest, feedbackId: string) => {
      if (!productId) {
        throw new Error("Missing productId for keyword feedback PUT");
      }
      await api.products.upsertKeywordFeedback(productId, feedbackId, body);
    },
    [api, productId],
  );

  const submit = useCallback(
    async (keyword: string, details?: KeywordFeedbackDetails) => {
      const key = keyForKeyword(keyword);
      const feedbackId = ensureFeedbackId(key);
      const previous = entriesRef.current.get(key);
      const optimistic: KeywordFeedbackEntry = {
        feedbackId,
        details,
        discarded: false,
      };

      setEntries((state) => cloneWith(state, key, optimistic));

      try {
        const body: KeywordFeedbackRequest = { keyword, discarded: false };
        if (details) body.feedback = details;
        await put(body, feedbackId);
      } catch (error) {
        logError(error);
        setEntries((state) =>
          previous ? cloneWith(state, key, previous) : cloneWithout(state, key),
        );
        toast.error(
          t("components:keywordsTab.keywordFeedbackPopover.toasts.errorUpdate"),
        );
        throw error;
      }
    },
    [ensureFeedbackId, put, t],
  );

  const discard = useCallback(
    async (keyword: string) => {
      const key = keyForKeyword(keyword);
      const previous = entriesRef.current.get(key);
      if (!previous) return;
      const { feedbackId } = previous;
      // Clear `details` on the local entry so re-flagging the same keyword
      // opens the editor again rather than landing in summary mode for
      // categorization the user just explicitly removed.
      const optimistic: KeywordFeedbackEntry = {
        feedbackId,
        details: undefined,
        discarded: true,
      };

      setEntries((state) => cloneWith(state, key, optimistic));

      try {
        // Still send the prior details on the discard PUT so the Firestore
        // log keeps the user's categorization (the local state forgets it).
        const body: KeywordFeedbackRequest = { keyword, discarded: true };
        if (previous.details) body.feedback = previous.details;
        await put(body, feedbackId);
      } catch (error) {
        logError(error);
        setEntries((state) => cloneWith(state, key, previous));
        toast.error(
          t("components:keywordsTab.keywordFeedbackPopover.toasts.errorUpdate"),
        );
        throw error;
      }
    },
    [put, t],
  );

  const clear = useCallback(() => {
    setEntries(new Map());
    idsRef.current = new Map();
  }, []);

  const getEntry = useCallback(
    (keyword: string): KeywordFeedbackEntry | undefined =>
      entries.get(keyForKeyword(keyword)),
    [entries],
  );

  const isFlagged = useCallback(
    (keyword: string): boolean => {
      const entry = entries.get(keyForKeyword(keyword));
      return entry !== undefined && !entry.discarded;
    },
    [entries],
  );

  return { submit, discard, clear, getEntry, isFlagged };
};

export type UseKeywordFeedbackReturn = ReturnType<typeof useKeywordFeedback>;
